<template>
	<div
		v-if="!isPopupOpen || !inModal"
		class="row row-col-2 gx-2 px-2 p-0 my-0"
		:class="{
			'justify-content-between pt-3': !inModal,
			'justify-content-center': inModal,
		}"
	>
		<div v-if="!inModal" class="col-auto">
			<legend>
				<div class="text-left text-nowrap">
					Запуск&nbsp;/&nbsp;зупинка силового модуля станції
				</div>
			</legend>
		</div>

		<div class="col-auto">
			<button
				class="btn btn-secondary btn-sm mx-2"
				style="min-width: 8em;"
				:disabled="
					isPopupOpen
					|| station.getFormatedMetric(PowerUnit.metricKeys.stationState, 'power') === 1
					|| !isSupportedPowerUnitVersion
					|| !isEnabled
				"
				@click="onSubmit(true)"
			>
				<span
					v-if="isPopupOpen"
					class="spinner-border spinner-border-sm"
					role="status"
					aria-hidden="true">
				</span>
				Пуск
			</button>
			<button
				class="btn btn-secondary btn-sm mx-2"
				style="min-width: 8em;"
				:disabled="
					isPopupOpen
					|| station.getFormatedMetric(PowerUnit.metricKeys.stationState, 'power') === 0
					|| !isSupportedPowerUnitVersion
					|| !isEnabled
				"
				@click="onSubmit(false)"
			>
				<span
					v-if="isPopupOpen"
					class="spinner-border spinner-border-sm"
					role="status"
					aria-hidden="true">
				</span>
				Стоп
			</button>
		</div>

		<div v-if="!isSupportedPowerUnitVersion"
			class="col-12 text-nowrap text-secondary my-0 py-0 pt-2"
		>
			*&nbsp;Встановлений силовий модуль не може виконати цю дію
		</div>

		<div v-if="isSupportedPowerUnitVersion" 
			class="col-12 my-0 py-0"
		>
			<div id="valueHelpBlock" class="form-text">
				<p :class="helpPromtClass">{{ helpPromt }}&nbsp;</p>
			</div>
		</div>
	</div>

	<!-- Modal -->
	<confirmation-popup
		v-model:show="isPopupOpen"
		:confirm-button-title="isLoading || isError || allRpcs.length > 0 ? '' : 'Так'"
		:reject-button-title="isLoading || isError || allRpcs.length > 0 ? 'Закрити' : 'Ні'"
		@confirm="makeRequest()"
	>
		<template v-if="station.isActive" v-slot:body>
			<template v-if="isLoading">
				<span
					class="spinner-border spinner-border-sm"
					role="status"
					aria-hidden="true"
				>
				</span>
				Перевірка активних задач телекерування
			</template>
			<template v-else-if="isError">
				Помилка при перевірці активних задач телекерування, закрийте і спробуйте ще.
			</template>
			<template v-else>
				<template v-if="allRpcs.length > 0">
					Станція зараз обробляє інший запит 
					("{{ rpcTaskSchema.job.findJob(allRpcs[0][rpcTaskSchema.job.key]).title }}"). 
					Будь ласка дочекайтесь його виконання.
				</template>
				<template v-else>
					Запустити
					{{ targetState ? 'пуск' : 'зупинку' }}
					силового модуля станції зараз?
				</template>
			</template>
		</template>
		<template v-else v-slot:body>
			Станція не активна. Тільки активні станції виконують запити.
		</template>
	</confirmation-popup>
</template>


<script>
import ConfirmationPopup from "@/components/ConfirmationPopup.vue";
import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { useAccontStore } from '@/stores/AccountStore'
import { useStationsStore } from '@/stores/StationsStore'
import {
	rpcTaskSchema,
	stationManageJobs,
	useRpcTasks,
} from '@/composables/rpcTasks.js'

export default {
	components: {
		ConfirmationPopup,
	},

	props: {
		isEnabled: {
			type: Boolean,
			required: false,
			default: true,
		},
		stationId: {
			type: String,
			required: true,
		},
		inModal: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	emits: {
		// Request creation result
		requestResult: value => typeof value === 'boolean',
	},

	setup() {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const {
			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
		} = useRpcTasks()

		return {
			rpcTaskSchema,
			accountStore,
			stationsStore,
			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
			Station,
			PowerUnit,
		}
	},

	data() {
		return {
			//
			targetState: null,
			// Submit confirmation popup
			isPopupOpen: false,
			// Help promt
			helpPromtClass: '',
			helpPromt: '',
		}
	},

	mounted () {},

	methods: {
		onSubmit(targetState) {
			this.targetState = targetState
			this.checkActiveTask()
			this.isPopupOpen = true
		},

		checkActiveTask() {
			this.setFilters({
				destinationStationIds: this.stationId,
				isComplete: false,
				jobs: stationManageJobs,
			})
		},

		async makeRequest() {
			if (this.isLoading || this.isError || this.allRpcs.length > 0) {
				return
			}

			const result = await this.createRpc(
				this.accountStore.userId,
				rpcTaskSchema.job.allowed.setPowerUnitState.key,
				this.stationId,
				null,
				{ [rpcTaskSchema.context.state.key]: this.targetState },
			)

			if (result) {
				this.printHint('Запит надіслано. Спостерігайте за його станом.')
			} else {
				this.printErr('Сталась помилка. Сервер відхилив запит.')
			}

			this.$emit('requestResult', Boolean(result))
		},

		printHint (msg) {
			this.helpPromtClass = "text-secondary"
			this.helpPromt = msg
		},

		printErr (msg) {
			this.helpPromtClass = "text-danger"
			this.helpPromt = msg
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		isSupportedPowerUnitVersion () {
			// If no power unit (undefined)
			if (!this.station.passport.has(Station.passportKeys.powerUnit.self)) {
				return false
			}

			return [
				PowerUnit.versions.v2017,
				PowerUnit.versions.v2022,
				PowerUnit.versions.v2025,
				PowerUnit.versions.v2025t,
				PowerUnit.versions.mcps,
			].includes(this.station.passport.get(Station.passportKeys.powerUnit.self)[Station.passportKeys.powerUnit.unitVersion])
		}
	},
}
</script>


<style src="@/assets/stationForm.css" scoped></style>